import { z } from "zod";
import {
    COACH_ASSIGNMENT_SOURCE_TYPES,
    COACH_ASSIGNMENT_STATUSES,
    DASHBOARD_RANGES,
} from "./enums";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be an ISO date (YYYY-MM-DD)");
const time = z.string().regex(/^\d{2}:\d{2}$/, "Must be a time (HH:mm)");

export const loginSchema = z.object({
    identifier: z.string().trim().min(1, "Email or username is required"),
    password: z.string().min(6, "Password must be at least 6 characters"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const refreshSchema = z.object({
    refreshToken: z.string().trim().min(1, "Refresh token is required"),
});
export type RefreshInput = z.infer<typeof refreshSchema>;

export const googleLoginSchema = z.object({
    idToken: z.string().trim().min(1, "Google ID token is required"),
});
export type GoogleLoginInput = z.infer<typeof googleLoginSchema>;

/**
 * Self-service coach signup. Email + password are mandatory; the coach must
 * also give a name and a phone number so the profile is usable for scheduling.
 * `googleIdToken` is optional and links the fresh account to a Google identity
 * so "Continue with Google" works on the next visit.
 */
export const registerSchema = z.object({
    fullName: z
        .string()
        .trim()
        .min(2, "Full name is required")
        .max(120, "Full name is too long"),
    phone: z
        .string()
        .trim()
        .min(8, "Phone number is required")
        .max(24, "Phone number is too long")
        .regex(/^\+?[0-9][0-9\s-]{6,22}$/, "Phone number may only contain digits, spaces, + or -"),
    email: z
        .string()
        .trim()
        .min(1, "Email is required")
        .email("Enter a valid email address")
        .max(160, "Email is too long"),
    password: z
        .string()
        .min(8, "Password must be at least 8 characters")
        .max(128, "Password is too long"),
    /** Optional Firebase/Google ID token used to link the new account. */
    googleIdToken: z.string().trim().min(1).optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

/** Verifikasi OTP 6-digit yang dikirim via Company System (`otp.email.coach_login`). */
export const verifyEmailSchema = z.object({
    email: z.string().trim().min(1).email().max(160),
    code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code"),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendOtpSchema = z.object({
    email: z.string().trim().min(1).email().max(160),
});
export type ResendOtpInput = z.infer<typeof resendOtpSchema>;

export const coachPlanQuerySchema = z.object({
    from: isoDate,
    to: isoDate,
    outletId: z.string().uuid().optional(),
    status: z.enum(COACH_ASSIGNMENT_STATUSES).optional(),
    sourceType: z.enum(COACH_ASSIGNMENT_SOURCE_TYPES).optional(),
});
export type CoachPlanQueryInput = z.infer<typeof coachPlanQuerySchema>;

/**
 * Dashboard coach — agregasi statistik & chart. `date` adalah anchor
 * (default: hari ini di server) supaya FE bisa melihat minggu/bulan lain
 * tanpa harus menghitung rentang sendiri.
 */
export const coachDashboardQuerySchema = z.object({
    range: z.enum(DASHBOARD_RANGES).default("week"),
    date: isoDate.optional(),
});
export type CoachDashboardQueryInput = z.infer<typeof coachDashboardQuerySchema>;

/**
 * Menu Assignment — daftar tabel penugasan coach (berbeda dengan `/coach/plan`
 * yang mengembalikan kalender harian). Filter sama, hanya ditambah `limit`.
 */
export const coachAssignmentListQuerySchema = coachPlanQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(200).default(100),
    upcomingOnly: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => value === "true"),
});
export type CoachAssignmentListQueryInput = z.infer<typeof coachAssignmentListQuerySchema>;

/**
 * Menu Payroll — ringkasan estimasi per bulan. `month` `YYYY-MM`
 * (opsional; FE selalu mengirim bulan yang dipilih, default bulan berjalan
 * bila parameter kosong).
 */
export const coachPayrollQuerySchema = z.object({
    month: z
        .string()
        .regex(/^\d{4}-\d{2}$/, "Must be a month (YYYY-MM)")
        .optional(),
});
export type CoachPayrollQueryInput = z.infer<typeof coachPayrollQuerySchema>;

/**
 * Keputusan coach atas undangan afiliasi — hanya dua arah, dan hanya untuk
 * permintaan ber-jalur `coach`.
 */
export const affiliationDecisionSchema = z.object({
    decision: z.enum(["accept", "decline"]),
});
export type AffiliationDecisionInput = z.infer<typeof affiliationDecisionSchema>;

/**
 * Menu Profile — coach boleh mengubah data tampilannya sendiri. Email &
 * `coach_name` sengaja TIDAK bisa diubah (identitas login & data merchant).
 */
export const updateCoachProfileSchema = z.object({
    displayName: z.string().trim().min(2, "Name is required").max(120).optional(),
    phone: z
        .string()
        .trim()
        .min(8, "Phone number is required")
        .max(24, "Phone number is too long")
        .regex(/^\+?[0-9][0-9\s-]{6,22}$/, "Phone number may only contain digits, spaces, + or -")
        .optional(),
    bio: z.string().trim().max(500, "Bio is too long").optional().nullable(),
});
export type UpdateCoachProfileInput = z.infer<typeof updateCoachProfileSchema>;

/**
 * Menu Sport — coach hanya mengelola olahraganya sendiri (`coach_sports`),
 * bukan master `sports` (itu ranah merchant/core).
 */
export const createCoachSportSchema = z.object({
    sportId: z.string().uuid("Sport is required"),
    isPrimary: z.boolean().optional(),
});
export type CreateCoachSportInput = z.infer<typeof createCoachSportSchema>;

export const updateCoachSportSchema = z.object({
    isPrimary: z.boolean(),
});
export type UpdateCoachSportInput = z.infer<typeof updateCoachSportSchema>;

export const updateAssignmentStatusSchema = z.object({
    status: z.enum(COACH_ASSIGNMENT_STATUSES),
    notes: z.string().trim().max(2000).optional(),
});
export type UpdateAssignmentStatusInput = z.infer<typeof updateAssignmentStatusSchema>;

export { isoDate, time };
